import IconMenu from '@picsart/icons/IconMenu';
import IconCrossLarge from '@picsart/icons/IconCrossLarge';
import IconSearch from '@picsart/icons/IconSearch';
import IconChevronLeftLarge from '@picsart/icons/IconChevronLeftLarge';
import cls from 'classnames';
import { memo, useState, useEffect, useCallback } from 'react';

import { SingleToolSearch } from '../../SingleToolSearch';
import { ISingleToolNavigationProps } from '../types';
import { NAVBAR_LINKS } from '../../../../data/nav/';

import useStyles from './styles';
import NavHeader from './NavHeader';
import { NavBody } from './NavBody';

const firstLeveItems = NAVBAR_LINKS.map((item) => ({
  id: item.id,
  text: item.label,
  icon: {
    src: item.icon,
    width: 24,
    height: 24,
    fill: '#666',
    background: 'none',
  },
  hasSublinks: true,
}));

const secondLevelItems = NAVBAR_LINKS.map(
  ({ id, subLinks, fillColor, iconColor, label, bottomLinks }) => {
    return {
      header: label,
      id: id,
      iconFill: fillColor,
      iconBackground: iconColor,
      bottomLinks,
      items: subLinks.map((link) => ({
        id: link.id,
        icon: link.icon,
        hasSublinks: Boolean(link.subCategories),
        text: link.name,
        href: link.href,
        iconRound: link.iconRound,
      })),
    };
  },
);

const thirdLevelItems = NAVBAR_LINKS.map(({ subLinks }) =>
  subLinks
    .filter(({ subCategories }) => subCategories)
    .map(({ subCategories, name, id, bottomLinks }) => ({
      header: name,
      id,
      bottomLinks,
      items: subCategories.map((subCategory) => ({
        id: subCategory.id,
        text: subCategory.label,
        href: subCategory.href,
        hasSublinks: false,
      })),
    })),
);

function SingleToolNavigationMobile({
  analytics,
  pageName,
  toolName,
}: Omit<ISingleToolNavigationProps, 'items'>) {
  const classes = useStyles();
  const [allVisible, setAllVisible] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [visibleSubmenus, setVisibleSubmenus] = useState<string[]>([]);
  const [searchVisible, setSearchVisible] = useState(false);

  const closeMenu = useCallback(() => {
    setVisibleSubmenus([]);
    setAllVisible(false);
  }, []);

  const goUpALevel = useCallback((e) => {
    e.stopPropagation();
    setVisibleSubmenus((prev) => {
      prev.pop();

      return [...prev];
    });
  }, []);

  const goDownTo = useCallback(
    (id) => (e) => {
      e.stopPropagation();
      setVisibleSubmenus((prev) => {
        prev.push(id);

        return [...prev];
      });
    },
    [],
  );

  useEffect(() => {
    if (allVisible) {
      setOverlayVisible(allVisible);
      setTimeout(() => {
        setMenuVisible(allVisible);
      });
      return;
    }

    setMenuVisible(allVisible);
    setTimeout(() => {
      setOverlayVisible(allVisible);
    }, 500);
  }, [allVisible]);

  return (
    <>
      <IconMenu
        className={cls(classes.icon, classes.iconMenu)}
        onClick={(e) => {
          e.stopPropagation();
          setAllVisible(true);
        }}
      />
      <div className={cls(classes.root, overlayVisible && classes.visible)} onClick={closeMenu}>
        <div
          className={cls(classes.submenu, menuVisible && classes.submenuVisible)}
          onClick={(e) => e.stopPropagation()}
        >
          <NavHeader
            baseIconClassName={classes.icon}
            text="Menu"
            leftIcon={IconCrossLarge}
            rightIcon={IconSearch}
            onLeftIconClick={() => setAllVisible(false)}
            onRightIconClick={() => setSearchVisible(true)}
          />

          <NavBody items={firstLeveItems} goDownTo={goDownTo} arrowColor="#C209C1" />
        </div>

        <div
          className={cls(classes.submenu, searchVisible && classes.submenuVisible)}
          onClick={(e) => e.stopPropagation()}
        >
          <NavHeader
            baseIconClassName={classes.icon}
            text="Search"
            leftIcon={IconChevronLeftLarge}
            rightIcon={IconSearch}
            onLeftIconClick={() => setSearchVisible(false)}
          />

          <div>
            <SingleToolSearch
              analytics={analytics}
              toolName={toolName}
              className={classes.search}
              pageName={pageName}
            />
          </div>
        </div>

        {secondLevelItems.map(({ header, id, iconFill, iconBackground, bottomLinks, items }) => {
          return (
            <div
              key={id}
              className={cls(classes.submenu, id === visibleSubmenus[0] && classes.submenuVisible)}
              onClick={(e) => e.stopPropagation()}
            >
              <NavHeader
                baseIconClassName={classes.icon}
                text={header}
                leftIcon={IconChevronLeftLarge}
                onLeftIconClick={goUpALevel}
              />
              <NavBody
                items={items}
                goDownTo={goDownTo}
                arrowColor="#000"
                iconFill={iconFill}
                iconBackground={iconBackground}
                bottomLinks={bottomLinks}
              />
            </div>
          );
        })}

        {thirdLevelItems.map((list) =>
          list.map(({ header, id, items, bottomLinks }) => {
            return (
              <div
                key={id}
                onClick={(e) => e.stopPropagation()}
                className={cls(
                  classes.submenu,
                  id === visibleSubmenus[1] && classes.submenuVisible,
                )}
              >
                <NavHeader
                  baseIconClassName={classes.icon}
                  text={header}
                  leftIcon={IconChevronLeftLarge}
                  onLeftIconClick={goUpALevel}
                />
                <NavBody
                  className={classes.thirdLevel}
                  items={items}
                  goDownTo={goDownTo}
                  arrowColor="#000"
                  bottomLinks={bottomLinks}
                />
              </div>
            );
          }),
        )}
      </div>
    </>
  );
}

export default memo(SingleToolNavigationMobile);
